import { z } from 'zod';

/**
 * Vite env schema for the agent-runtime SPA.
 *
 * Validated at module load. If any required var is missing or malformed the
 * app fails fast with a clear error — we'd rather crash at boot than ship a
 * broken bundle to prod.
 *
 * This SPA is unauthenticated: no Cognito config, no user-pool IDs. The only
 * runtime-sensitive var is VITE_API_BASE_URL, which is not a secret (it's the
 * public API Gateway URL). See docs/phase2.md §1 + §4.
 *
 * VITE_DEV_TENANT_SLUG / VITE_DEV_AGENT_SLUG are dev-only escape hatches that
 * let you run the SPA at http://localhost:5175/ without the real
 * `<tenant>.agent.cloneai.ca/<agent>` URL. Leave them unset in prod builds.
 */
const EnvSchema = z.object({
  VITE_APP: z.literal('agent-runtime'),
  VITE_APP_ENV: z.enum(['dev', 'prod']).default('dev'),
  VITE_API_BASE_URL: z.string().url(),
  /** Dev-only override for host-based slug parsing (placeholder mode). */
  VITE_DEV_TENANT_SLUG: z.string().optional(),
  /** Dev-only override for path-based slug parsing (placeholder mode). */
  VITE_DEV_AGENT_SLUG: z.string().optional(),
  /**
   * Public Sentry DSN for the cloneai-agent project. Build-time only, not a
   * secret per Sentry's security model. Empty string disables Sentry.
   */
  VITE_SENTRY_DSN: z.string().default(''),
  /**
   * Release identifier (git sha in CI, `'local'` otherwise). Used for
   * source-map upload correlation via `@sentry/vite-plugin`.
   */
  VITE_SENTRY_RELEASE: z.string().default('local'),
});

export type Env = z.infer<typeof EnvSchema>;

const parsed = EnvSchema.safeParse(import.meta.env);

if (!parsed.success) {
  console.error('[env] Invalid Vite env:', parsed.error.flatten().fieldErrors);
  throw new Error('Invalid Vite env — check .env.* files for the agent-runtime app');
}

export const env: Env = Object.freeze(parsed.data);
